import { NodeDisplayData, PartialButFor, PlainObject } from "sigma/types";
import { Settings } from "sigma/settings";

const TEXT_COLOR = "#000000";

/**
 * This function draw in the input canvas 2D context a rectangle.
 * It only deals with tracing the path, and does not fill or stroke.
 */
export function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Custom hover renderer
 */
export function drawHover(context: CanvasRenderingContext2D, data: PlainObject, settings: PlainObject) {
  const size = settings.labelSize;
  const font = settings.labelFont;
  const weight = settings.labelWeight;
  const descLabel_1_Size = size - 2;
  const descLabel_2_Size = size - 2;
  const descLabel_3_Size = size - 2;
  const descLabel_4_Size = size - 2;


  const label = data.label;
  const descLabel_1_ =   data.desc_1;
  const descLabel_2_ =   data.desc_2;
  const descLabel_3_ =   data.desc_3;
  const descLabel_4_ =   data.desc_4;
  const clusterLabel = data.desc;

  // Then we draw the label background
  context.beginPath();
  context.fillStyle = "#fff";
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 2;
  context.shadowBlur = 8;
  context.shadowColor = "#000";

  context.font = `${weight} ${size}px ${font}`;
  const labelWidth = context.measureText(label).width;
  
  context.font = `${weight} ${descLabel_1_Size}px ${font}`;
  const descLabel_1_Width = descLabel_1_ ? context.measureText(descLabel_1_).width : 0;
  context.font = `${weight} ${descLabel_1_Size}px ${font}`;
  
  context.font = `${weight} ${descLabel_2_Size}px ${font}`;
  const descLabel_2_Width = descLabel_2_ ? context.measureText(descLabel_2_).width : 0;
  context.font = `${weight} ${descLabel_2_Size}px ${font}`;
  
  context.font = `${weight} ${descLabel_3_Size}px ${font}`;
  const descLabel_3_Width = descLabel_3_ ? context.measureText(descLabel_3_).width : 0;
  context.font = `${weight} ${descLabel_3_Size}px ${font}`;
  
  context.font = `${weight} ${descLabel_4_Size}px ${font}`;
  const descLabel_4_Width = descLabel_4_ ? context.measureText(descLabel_4_).width : 0;
  context.font = `${weight} ${descLabel_4_Size}px ${font}`;

  const clusterLabelWidth = clusterLabel ? context.measureText(clusterLabel).width : 0;

  const textWidth = Math.max(labelWidth, descLabel_1_Width, descLabel_2_Width, descLabel_3_Width, descLabel_4_Width, clusterLabelWidth);

  const x = Math.round(data.x);
  const y = Math.round(data.y);
  const w = Math.round(textWidth + size / 2 + data.size + 3);
  const hLabel = Math.round(size / 2 + 4);
  const hdescLabel_1_ = descLabel_1_ ? Math.round(descLabel_1_Size / 2 + 9) : 0;
  const hdescLabel_2_ = descLabel_2_ ? Math.round(descLabel_2_Size / 2 + 9) : 0;
  const hdescLabel_3_ = descLabel_3_ ? Math.round(descLabel_3_Size / 2 + 9) : 0;
  const hdescLabel_4_ = descLabel_4_ ? Math.round(descLabel_4_Size / 2 + 9) : 0;
  const hClusterLabel = Math.round(descLabel_1_Size / 2 + 9);

  drawRoundRect(context, x, y - 12, w, hLabel + hdescLabel_1_ + hdescLabel_2_ + hdescLabel_3_ + hdescLabel_4_ + 20, 5);
  context.closePath();
  context.fill();

  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 0;

  // And finally we draw the labels
  // MAIN LABEL
  context.fillStyle = TEXT_COLOR;
  context.font = `${weight} ${size}px ${font}`;
  context.fillText(label, data.x + data.size + 3, data.y + hLabel - 3);
  
  // DESCRIPTION
  if (descLabel_1_) {
    context.fillStyle = data.color;
    context.font = `${weight} ${descLabel_1_Size}px ${font}`;
    context.fillText(descLabel_1_, data.x + data.size + 3, data.y + hLabel + hdescLabel_1_);
  }
  
  if (descLabel_2_) {
    context.fillStyle = data.color;
    context.font = `${weight} ${descLabel_2_Size}px ${font}`;
    context.fillText(descLabel_2_, data.x + data.size + 3, data.y + hLabel + hdescLabel_1_ + hdescLabel_2_);
  }
  
  if (descLabel_3_) {
    context.fillStyle = data.color;
    context.font = `${weight} ${descLabel_3_Size}px ${font}`;
    context.fillText(descLabel_3_, data.x + data.size + 3, data.y + hLabel + hdescLabel_1_ + hdescLabel_2_ + hdescLabel_3_);
  }

  if (descLabel_4_) {
    context.fillStyle = data.color;
    context.font = `${weight} ${descLabel_4_Size}px ${font}`;
    context.fillText(descLabel_4_, data.x + data.size + 3, data.y + hLabel + hdescLabel_1_ + hdescLabel_2_ + hdescLabel_3_ + hdescLabel_4_);
  }

}

/**
 * Custom label renderer
 */
export default function drawLabel(
  context: CanvasRenderingContext2D,
  data: PartialButFor<NodeDisplayData, "x" | "y" | "size" | "label" | "color">,
  settings: Settings,
): void {
  if (!data.label) return;

  const size = settings.labelSize,
    font = settings.labelFont,
    weight = settings.labelWeight;

  context.font = `${weight} ${size}px ${font}`;
  const width = context.measureText(data.label).width + 8;

  context.fillStyle = "#ffffffcc";
  context.fillRect(data.x + data.size, data.y + size / 3 - 15, width, 20);

  context.fillStyle = "#000";
  context.fillText(data.label, data.x + data.size + 3, data.y + size / 3);
}
